import { ConfigRequestOptions, downloadConfig, downloadTranslations } from 'providers/useConfig/ConfigApiIntegration';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getProcessEnv } from 'utils/fnEnvironments';

import ccConfig from 'config/config-cc.json';

import localTranslationsEn from 'config/translations-en.json';
import localTranslationsEt from 'config/translations-et.json';
import localTranslationsRu from 'config/translations-ru.json';
import { useApp } from 'providers/useApp/AppContext';
import { getLocalStorage, setLocalStorage } from 'utils/fnStorage';
import { usePersistentState } from 'hooks/useStorage/useStorage';
import { LocalStorage } from 'types/Storage';
import ApplicationConfig from './ApplicationConfig';
import { readConfigCache, writeConfigCache } from './configCache';

const LOCAL_TRANSLATION = {
    en: localTranslationsEn,
    et: localTranslationsEt,
    ru: localTranslationsRu,
};

const UPDATE_CONFIG_TIME = 'UPDATE_CONFIG_TIME';

const CONFIG_UPDATE_APPLIED = 'CONFIG_UPDATE_APPLIED';

const REFRESH_DEADLINE_MS = 60000;

const useConfigService = () => {
    const [translations, setTranslations] = useState<{ [key: string]: string }>(null);
    const [config, setConfig] = useState<typeof ApplicationConfig>(null);
    const [configError, setConfigError] = useState<boolean>(false);
    const [configRetrying, setConfigRetrying] = useState<boolean>(false);
    const { appLanguage, setApplicationLanguage } = useApp();
    const updateConfigTimeTimeout = useRef<number>(null);
    const configLoading = useRef<boolean>(false);
    const appliedConfigJson = useRef<string>(null);

    const { set: setUpdateConfigTime, store: updateConfigTime } = usePersistentState(UPDATE_CONFIG_TIME as keyof LocalStorage, -1);

    const currentUILanguage = useRef<string>(appLanguage);

    const forceLocalConfig = getLocalStorage('forceLocalConfig');
    const useLocalConfig: boolean = getProcessEnv('USE_LOCAL_CONFIG', 'false') === 'true';

    const getRandomIntInclusive = (min, max) => {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min + 1) + min);
    };

    const getLanguage = async (language, langUrl, forceReload = false, requestOptions: ConfigRequestOptions = {}) => {
        // if the translation is already loaded (skip the cache when a refresh is forced,
        // otherwise updated translations from CC would never be picked up)
        if (!forceReload && config?.app_config?.languages?.[currentUILanguage.current]?.data) {
            setTranslations(config.app_config.languages[currentUILanguage.current].data);
            return config.app_config.languages[currentUILanguage.current].data;
        }

        // need to load the translation resources
        let languageFile = LOCAL_TRANSLATION[language];

        if (!useLocalConfig && !forceLocalConfig) {
            try {
                languageFile = await downloadTranslations(langUrl, forceReload, requestOptions);
            } catch (e) {
                console.error('loading remote translation', e);
            }
        }

        if (languageFile?.data) {
            setTranslations(languageFile.data);
            return languageFile.data;
        }

        setTranslations({ language });
        return language;
    };

    const changeLanguage = async (language, configData = config, forceReload = false, requestOptions: ConfigRequestOptions = {}) => {
        currentUILanguage.current = language;
        if (config?.app_config?.general) {
            config.app_config.general.language = language;
        }

        const loadedLanguage = await getLanguage(language, configData?.app_config?.languages?.[language]?.url, forceReload, requestOptions);
        setLocalStorage('appLanguage', language);
        setApplicationLanguage(language);
        return loadedLanguage;
    };

    // rescheduled on every outcome, so one failed refresh cannot end the refresh chain for the session
    const scheduleNextConfigUpdate = (failed: boolean) => {
        const apiConfig = ApplicationConfig.api_config;
        const interval = failed
            ? apiConfig.backend_error_update_interval
            : (apiConfig.update_interval / 100) * getRandomIntInclusive(-5, 5) + apiConfig.update_interval;

        setUpdateConfigTime(Date.now() + interval);
    };

    const loadConfig = async (forceReload = false) => {
        if (configLoading.current) {
            return false;
        }

        configLoading.current = true;
        let loadFailed = false;

        try {
            // nothing waits on the periodic refresh, but it still may not hold configLoading for minutes
            const requestOptions: ConfigRequestOptions = forceReload ? { deadlineMs: REFRESH_DEADLINE_MS } : {};
            const skipRemote = useLocalConfig || forceLocalConfig;
            const cachedConfig = skipRemote ? null : readConfigCache();
            let rawConfig = ccConfig as { data: any };
            let isRemoteConfig = false;

            if (!skipRemote) {
                try {
                    const remoteConfig = await downloadConfig(requestOptions);

                    if (!remoteConfig?.data) {
                        throw new Error(`Config service returned no data${remoteConfig?.code ? ` (${remoteConfig.code})` : ''}`);
                    }

                    rawConfig = remoteConfig;
                    isRemoteConfig = true;
                } catch (e) {
                    rawConfig = { ...ccConfig, data: cachedConfig || (ccConfig as { data: any }).data };
                    console.error('loading remote config', e);
                }

                // we wanted a remote config and did not get one, so check back soon rather than in a day
                loadFailed = !isRemoteConfig;
            }

            // snapshot before createConfig runs: it mutates this payload in place, injecting the whole
            // translation dictionary through the by-reference app_config.languages it keeps
            const loadedConfigJson = JSON.stringify(rawConfig.data);

            const translationData = await changeLanguage(appLanguage, rawConfig.data, forceReload, requestOptions);

            const configData = ApplicationConfig.createConfig(rawConfig.data, appLanguage, translationData);

            // cached only once createConfig has proven the payload actually applies, and from the clean
            // snapshot rather than the object createConfig has just mutated
            if (isRemoteConfig) {
                writeConfigCache(JSON.parse(loadedConfigJson));
            }

            setConfig(configData);
            setConfigError(false);

            // compared against what this tab actually applied, so another tab refreshing the shared
            // cache first cannot suppress a reload this one still needs
            const changed = isRemoteConfig && appliedConfigJson.current !== null && loadedConfigJson !== appliedConfigJson.current;

            appliedConfigJson.current = loadedConfigJson;

            return changed;
        } catch (e) {
            console.error('applying configuration', e);
            setConfigError(true);
            loadFailed = true;

            return false;
        } finally {
            configLoading.current = false;
            scheduleNextConfigUpdate(loadFailed);
        }
    };

    const retryConfig = async () => {
        // a refresh already in flight would make loadConfig a no-op, and clearing configError first would
        // drop the user onto a bare splash with nothing running on their behalf
        if (configLoading.current || configRetrying) {
            return;
        }

        setConfigRetrying(true);

        try {
            await loadConfig();
        } finally {
            setConfigRetrying(false);
        }
    };

    useEffect(() => {
        const now = Date.now();
        if (typeof updateConfigTime === 'number' && updateConfigTime && updateConfigTime > now) {
            clearTimeout(updateConfigTimeTimeout.current);
            updateConfigTimeTimeout.current = window.setTimeout(() => {
                loadConfig(true).then((changed) => {
                    // only force the reload in Application when the config actually differs
                    if (changed) {
                        setLocalStorage(CONFIG_UPDATE_APPLIED as keyof LocalStorage, false);
                    }
                });
            }, updateConfigTime - now);
        }

        return () => clearTimeout(updateConfigTimeTimeout.current);
    }, [updateConfigTime]);

    useEffect(() => {
        if (!config && !translations && !configError) {
            loadConfig();
        }

        if (config && !translations) {
            changeLanguage(appLanguage).catch(() => {});
        }

        if (config && translations) {
            if (!config.app_config.languages[currentUILanguage.current]) {
                // if the language configuration is missing from the config create a placeholder object
                // where the translation content to be loaded
                config.app_config.languages[currentUILanguage.current] = {};
            }
            config.app_config.languages[currentUILanguage.current].data = translations;
        }
    }, [config, translations, configError, currentUILanguage.current]);

    return {
        config,
        changeLanguage,
        translations,
        configError,
        configRetrying,
        retryConfig,
    };
};

export const ConfigContext = createContext<ReturnType<typeof useConfigService>>(null);

export const ConfigProvider = ({ children }) => {
    const Config = useConfigService();

    return <ConfigContext.Provider value={Config}>{children}</ConfigContext.Provider>;
};

export const useConfig = () => useContext(ConfigContext);
